// The filtering behind CarBrowser, kept free of React so it reads (and could be
// tested) on its own. Everything runs over one event's cars, which are already
// on the page.
import { CarCardCar, ColorFamilyOption } from '@/types/shared'

/** What filtering needs from a car: the card facts plus its labelled specs. */
export type FilterableCar = CarCardCar & { specs: { label: string; value: string }[] }

export interface CarFilters {
  /** Color family keys; a car matches any of them. */
  colors: string[]
  /** A normalized make key (see `normalize`), or null for any. */
  make: string | null
  model: string | null
  query: string
}

export const NO_FILTERS: CarFilters = { colors: [], make: null, model: null, query: '' }

export interface Facet {
  key: string
  label: string
  count: number
}

// Make and model are free text, so "nissan", "Nissan" and "Nissan " are one
// make. Grouped on this, shown in whichever spelling most people used.
export const normalize = (value: string | null | undefined) => (value ?? '').trim().toLowerCase()

export function filtersActive(filters: CarFilters): boolean {
  return filters.colors.length > 0 || filters.make !== null || filters.model !== null || filters.query.trim() !== ''
}

function facetsBy(cars: FilterableCar[], valueOf: (car: FilterableCar) => string | null): Facet[] {
  const groups = new Map<string, { count: number; spellings: Map<string, number> }>()
  cars.forEach((car) => {
    const raw = (valueOf(car) ?? '').trim()
    const key = normalize(raw)
    if (!key) return
    const group = groups.get(key) ?? { count: 0, spellings: new Map() }
    group.count += 1
    group.spellings.set(raw, (group.spellings.get(raw) ?? 0) + 1)
    groups.set(key, group)
  })

  return [...groups.entries()]
    .map(([key, { count, spellings }]) => ({
      key,
      count,
      label: [...spellings.entries()].sort((a, b) => b[1] - a[1])[0][0],
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export const makeFacets = (cars: FilterableCar[]) => facetsBy(cars, (car) => car.make)

/** Models of the chosen make only — a model list across every make is noise. */
export const modelFacets = (cars: FilterableCar[], make: string | null) =>
  make === null ? [] : facetsBy(cars.filter((car) => normalize(car.make) === make), (car) => car.model)

/** The colors present at this event, most common first. */
export function colorFacets(cars: FilterableCar[]): { color: ColorFamilyOption; count: number }[] {
  const counts = new Map<string, { color: ColorFamilyOption; count: number }>()
  cars.forEach((car) => {
    if (!car.color_family) return
    const entry = counts.get(car.color_family.value) ?? { color: car.color_family, count: 0 }
    entry.count += 1
    counts.set(car.color_family.value, entry)
  })
  return [...counts.values()].sort((a, b) => b.count - a.count || a.color.label.localeCompare(b.color.label))
}

/**
 * Search matches every word against the things people half-remember: the
 * driver, their handle, the car, and the specs ("work meister", "s13").
 */
function matchesQuery(car: FilterableCar, query: string): boolean {
  const words = normalize(query).split(/\s+/).filter(Boolean)
  if (words.length === 0) return true

  const haystack = [car.driver_name, car.instagram, car.make, car.model, car.year?.toString(), ...car.specs.map((s) => s.value)]
    .map(normalize)
    .join(' ')
  return words.every((word) => haystack.includes(word.replace(/^@/, '')))
}

export function carMatches(car: FilterableCar, filters: CarFilters): boolean {
  if (filters.colors.length > 0 && !(car.color_family && filters.colors.includes(car.color_family.value))) return false
  if (filters.make !== null && normalize(car.make) !== filters.make) return false
  if (filters.model !== null && normalize(car.model) !== filters.model) return false
  return matchesQuery(car, filters.query)
}
